import { useState } from 'react';
import { useApp } from '../state/store';
import { back } from '../lib/router';
import { C, R, font } from '../theme/tokens';
import { photoUrl } from '../data/photos';
import { PLATFORMS, campaignUrl, captionWithTags, copyText, intentUrl, isHttpUrl, nativeShare, type Platform } from '../services/share';
import { BackPill, Btn, Card, EmptyState, Eyebrow, FieldError, Mono, Photo, Tag, inputStyle, safeTop } from '../components/ui';

export function ShareKitScreen({ id }: { id: string }) {
  const { state: { data }, act } = useApp();
  const [kitId, setKitId] = useState(id);
  const [assetIdx, setAssetIdx] = useState(0);
  const [variant, setVariant] = useState<'short' | 'story'>('short');
  const [status, setStatus] = useState('');
  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const kit = data.shareKits[kitId];
  if (!kit) return <EmptyState title="Share kit not found." body="It may have been withdrawn or the link is out of date." action={<Btn variant="primary" onClick={back}>Back</Btn>} />;

  const asset = kit.assets[Math.min(assetIdx, kit.assets.length - 1)];
  const url = campaignUrl(kit);
  const caption = captionWithTags(kit, variant);
  const records = data.shareRecords.filter((r) => r.kitId === kit.id);
  const usedOn = new Set(records.map((r) => r.platform));

  async function toPlatform(p: Platform) {
    const intent = intentUrl(p, caption, url);
    if (intent) {
      window.open(intent, '_blank', 'noopener,noreferrer');
      setStatus(`${p} opened in a new tab with the caption and link. You publish it from your account.`);
    } else {
      const ok = await copyText(`${caption} ${url}`);
      setStatus(ok ? `Caption and link copied. Open ${p}, choose the ${asset.label.toLowerCase().includes('artwork') ? 'artwork' : 'photo'} and paste.` : 'Copy is blocked here. Select the caption above and copy it manually.');
    }
    act.recordShare(kit.id, p);
  }

  async function native() {
    const r = await nativeShare({ title: kit.label, text: caption, url, imageUrl: photoUrl(asset.photo, 1080) });
    if (r === 'shared') { setStatus('Shared from your phone share sheet.'); act.recordShare(kit.id, 'Share sheet'); }
    else if (r === 'cancelled') setStatus('Share cancelled.');
    else {
      const ok = await copyText(`${caption} ${url}`);
      setStatus(ok ? 'This browser has no share sheet. Caption and link copied instead.' : 'Sharing is not available here. Select the caption and copy it.');
    }
  }

  function record() {
    if (!isHttpUrl(link.trim())) { setLinkError('Paste the full post link, starting with https://'); return; }
    setLinkError('');
    act.recordShare(kit.id, 'Recorded link', link.trim());
    setLink('');
    act.toast('Post recorded');
  }

  return (
    <div className="slide-in">
      <div style={{ padding: `${safeTop(12)} 16px 0`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <BackPill onClick={back} />
        <Tag label={`APPROVED · ${kit.approvedBy.toUpperCase()}`} bg={C.greenBg} fg={C.green} />
      </div>
      <h1 style={{ margin: 0, padding: '16px 20px 0', font: font(800, 56, 0.86), letterSpacing: '-.06em' }}>Share kit</h1>
      <div className="noscroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '14px 20px 0' }}>
        {Object.values(data.shareKits).map((k) => {
          const on = k.id === kit.id;
          return (
            <button key={k.id} type="button" aria-pressed={on} onClick={() => { setKitId(k.id); setAssetIdx(0); setStatus(''); }}
              style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 14px 0 4px', borderRadius: R.pill, border: `1px solid ${on ? C.ink : C.border}`, background: on ? C.ink : C.white, color: on ? C.white : C.ink, font: font(600, 13), transition: 'all .2s' }}>
              <span style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', background: C.divider }}><Photo id={k.assets[0].photo} pad={2} sizes="36px" alt="" /></span>{k.label}
            </button>
          );
        })}
      </div>
      <div style={{ margin: '16px 12px 0', position: 'relative', height: 300, borderRadius: R.card, overflow: 'hidden', background: C.night }}>
        <Photo id={asset.photo} pad={40} eager />
        <span style={{ position: 'absolute', top: 12, left: 12 }}><Tag label={asset.label} bg={C.white} fg={C.ink} radius={R.pill} /></span>
      </div>
      {kit.assets.length > 1 && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 12px 0' }}>
          {kit.assets.map((x, i) => (
            <button key={x.photo} type="button" aria-label={`Use ${x.label}`} aria-pressed={i === assetIdx} onClick={() => setAssetIdx(i)}
              style={{ width: 76, height: 76, borderRadius: R.panel, overflow: 'hidden', border: `2px solid ${i === assetIdx ? C.violet : 'transparent'}`, padding: 0, background: C.night }}>
              <Photo id={x.photo} pad={4} sizes="76px" alt="" />
            </button>
          ))}
        </div>
      )}

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Eyebrow>Caption</Eyebrow>
          <div role="group" aria-label="Caption length" style={{ display: 'flex', gap: 4, background: C.subtle, borderRadius: R.pill, padding: 3 }}>
            {(['short', 'story'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={variant === v} onClick={() => setVariant(v)} className="hit"
                style={{ height: 30, padding: '0 12px', borderRadius: R.pill, border: 0, background: variant === v ? C.ink : 'transparent', color: variant === v ? C.white : C.ink, font: font(600, 12) }}>
                {v === 'short' ? 'Short' : 'Story'}
              </button>
            ))}
          </div>
        </div>
        <p style={{ margin: 0, font: font(400, 15, 1.45) }}>{kit.captions[variant]}</p>
        <Mono size={12} color={C.violet}>{kit.hashtags.join(' ')}</Mono>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: R.input, background: C.paper, border: `1px solid ${C.subtle}` }}>
          <Mono size={12} color={C.ink} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</Mono>
          <Btn size="xs" onClick={async () => setStatus((await copyText(url)) ? 'Campaign link copied.' : 'Copy is blocked here.')}>Copy link</Btn>
        </div>
        <Eyebrow style={{ marginTop: 4 }}>Post to</Eyebrow>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
          {PLATFORMS.map((p) => {
            const on = usedOn.has(p);
            return <Btn key={p} variant={on ? 'soft' : 'outline'} style={{ padding: 0 }} onClick={() => toPlatform(p)}>{on ? `${p} ✓` : p}</Btn>;
          })}
        </div>
        <Btn variant="primary" size="lg" full onClick={native} style={{ height: 52, font: font(700, 15) }}>Open phone share sheet</Btn>
        <span role="status" style={{ font: font(400, 12, 1.45), color: C.grey }}>{status || 'Assisted sharing: you publish from your own account. Approved media only.'}</span>
      </Card>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Eyebrow>Record your post (optional)</Eyebrow>
        <form onSubmit={(e) => { e.preventDefault(); record(); }} style={{ display: 'flex', gap: 8 }}>
          <input type="url" inputMode="url" aria-label="Post link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" aria-invalid={Boolean(linkError)} style={{ ...inputStyle, height: 46, flex: 1, minWidth: 0 }} />
          <Btn type="submit" variant="ink" style={{ height: 46 }}>Save</Btn>
        </form>
        {linkError && <FieldError>{linkError}</FieldError>}
        {records.filter((r) => r.url).map((r) => <Mono key={r.id} size={12} color={C.green}>{`✓ ${r.url!.replace(/^https?:\/\//, '').slice(0, 44)}`}</Mono>)}
        <span style={{ font: font(400, 11, 1.45), color: C.grey }}>Links help UNIT Marketing &amp; Events report campaign reach. UGAIN never posts on your behalf.</span>
      </Card>
    </div>
  );
}
